import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth, useUser } from '../auth/ClerkWrapper';
import api from '../utils/api';

const AppContext = createContext(null);

// Local storage key for active assignment
const ACTIVE_ASSIGNMENT_KEY = 'grademind-active-assignment';

export function AppProvider({ children }) {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const { user } = useUser();
  
  const [assignments, setAssignments] = useState([]);
  const [activeAssignmentId, setActiveAssignmentId] = useState(null);
  const [userUsage, setUserUsage] = useState(null);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);

  // Persist activeAssignmentId to localStorage (user-scoped)
  useEffect(() => {
    const key = user ? `${ACTIVE_ASSIGNMENT_KEY}-${user.id}` : null;
    if (activeAssignmentId && key) {
      localStorage.setItem(key, activeAssignmentId);
    } else if (key) {
      localStorage.removeItem(key);
    }
  }, [activeAssignmentId, user]);

  // Reset state when user identity changes to prevent cross-account leaks
  const prevUserIdRef = React.useRef(user?.id);
  useEffect(() => {
    if (user?.id && user.id !== prevUserIdRef.current) {
      setAssignments([]);
      setUserUsage(null);
      const key = user ? `${ACTIVE_ASSIGNMENT_KEY}-${user.id}` : null;
      setActiveAssignmentId(key ? localStorage.getItem(key) || null : null);
    }
    prevUserIdRef.current = user?.id;
  }, [user?.id]);

  // Core fetch function - separated so it can be called for initial load and manual refresh
  const fetchFromBackend = useCallback(async () => {
    if (!isSignedIn || !user) return;

    setIsLoadingAssignments(true);
    try {
      const token = await getToken();
      const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

      // Fetch assignments and usage in parallel
      const [assignmentsResponse, usageResponse] = await Promise.allSettled([
        api.get('/assignments', { headers: authHeaders }),
        api.get(`/packages/usage/${user.id}`, { headers: authHeaders })
      ]);

      if (assignmentsResponse.status === 'fulfilled' && assignmentsResponse.value.data?.assignments) {
        const backendAssignments = assignmentsResponse.value.data.assignments.map(a => ({
          id: a._id,
          config: {
            title: a.title,
            description: a.description,
            totalScore: a.totalPoints,
            assignmentFile: a.assignmentFile ? { name: a.assignmentFile.split('/').pop() } : undefined,
            rubricFile: a.rubricFile ? { name: a.rubricFile.split('/').pop() } : undefined,
            solutionFile: a.solutionFile ? { name: a.solutionFile.split('/').pop() } : undefined,
          },
          sections: (a.sections || []).map(s => ({
            ...s,
            id: s._id || s.id || crypto.randomUUID(),
            name: s.name || s.title || 'Section',
            students: s.students || []
          })),
          submissionCount: a.submissionCount || 0,
          createdAt: a.createdAt ? (typeof a.createdAt === 'number' ? a.createdAt : new Date(a.createdAt).getTime()) : Date.now(),
          backendId: a._id,
          processingStatus: a.processingStatus,
          isDemo: a.isDemo || false
        }));

        // Only update if the server returned data. If the server returned an empty array
        // but we already have assignments in state (e.g. a slow query during bulk uploads
        // resolved after local state was populated), preserve existing state.
        if (backendAssignments.length > 0) {
          setAssignments(backendAssignments);
        } else {
          // Server returned empty — fall back to localStorage before clearing state
          const assignmentKey = `grademind-assignments-${user.id}`;
          const savedAssignments = localStorage.getItem(assignmentKey);
          if (savedAssignments) {
            try {
              const parsed = JSON.parse(savedAssignments);
              if (parsed.length > 0) {
                // Keep local copy until server confirms empty on a future refresh
                setAssignments(parsed);
              } else {
                setAssignments(backendAssignments); // genuinely empty
              }
            } catch (e) {
              console.error('Failed to load saved assignments:', e);
              setAssignments(backendAssignments);
            }
          } else {
            setAssignments(backendAssignments); // genuinely empty, no local copy
          }
        }

        setActiveAssignmentId(prev => {
          if (prev && !backendAssignments.find(a => a.id === prev)) {
            return null;
          }
          return prev;
        });
      } else if (assignmentsResponse.status === 'rejected') {
        console.error('Error fetching assignments, falling back to localStorage:', assignmentsResponse.reason);
        const assignmentKey = `grademind-assignments-${user.id}`;
        const savedAssignments = localStorage.getItem(assignmentKey);
        if (savedAssignments) {
          try {
            setAssignments(JSON.parse(savedAssignments));
          } catch (e) {
            console.error('Failed to load saved assignments:', e);
          }
        }
        // If localStorage is also empty, preserve whatever is currently in state
        // rather than defaulting to [] — avoids flashing an empty workspace
        // while a slow query is still in-flight or Gemini retries are running.
      }

      if (usageResponse.status === 'fulfilled') {
        setUserUsage(usageResponse.value.data);
      } else {
        console.warn('Could not fetch user usage data:', usageResponse.reason);
      }
    } catch (error) {
      console.error('Unexpected error during fetch:', error);
    } finally {
      setIsLoadingAssignments(false);
    }
  }, [isSignedIn, user, getToken]);

  // Load assignments from backend when user signs in
  useEffect(() => {
    if (!isSignedIn || !user) return;
    fetchFromBackend();
  }, [isSignedIn, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save assignments to localStorage whenever they change
  useEffect(() => {
    if (isSignedIn && user) {
      const storageKey = `grademind-assignments-${user.id}`;
      const assignmentsToSave = assignments.map(a => ({
        ...a,
        config: a.config ? {
          ...a.config,
          assignmentFile: a.config.assignmentFile ? { name: a.config.assignmentFile.name } : undefined,
          rubricFile: a.config.rubricFile ? { name: a.config.rubricFile.name } : undefined,
          solutionFile: a.config.solutionFile ? { name: a.config.solutionFile.name } : undefined,
        } : a.config
      }));
      try {
        localStorage.setItem(storageKey, JSON.stringify(assignmentsToSave));
      } catch (error) {
        console.error('Failed to save assignments to localStorage:', error);
      }
    }
  }, [assignments, isSignedIn, user]);

  // Create assignment
  const createAssignment = useCallback(async (config) => {
    const formData = new FormData();
    formData.append('title', config.title);
    formData.append('totalPoints', config.totalScore || 100);
    formData.append('description', config.description || '');

    const base64ToBlob = (base64Data, mimeType) => {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: mimeType });
    };

    if (config.assignmentFile) {
      const blob = base64ToBlob(config.assignmentFile.data, config.assignmentFile.mimeType);
      formData.append('assignment', blob, config.assignmentFile.name);
    } else if (config.assignmentText) {
      formData.append('assignmentText', config.assignmentText);
    }

    if (config.rubricFile) {
      const blob = base64ToBlob(config.rubricFile.data, config.rubricFile.mimeType);
      formData.append('rubric', blob, config.rubricFile.name);
    } else if (config.rubric) {
      const blob = new Blob([config.rubric], { type: 'text/plain' });
      formData.append('rubric', blob, 'rubric.txt');
    }

    if (config.solutionFile) {
      const blob = base64ToBlob(config.solutionFile.data, config.solutionFile.mimeType);
      formData.append('solution', blob, config.solutionFile.name);
    } else if (config.solution) {
      const blob = new Blob([config.solution], { type: 'text/plain' });
      formData.append('solution', blob, 'solution.txt');
    }

    const sections = [{
      selectedModels: config.selectedModels || ['gemini-2.5-pro'],
      useAverageGrading: config.useAverageGrading || false
    }];
    formData.append('sections', JSON.stringify(sections));

    const token = await getToken();
    const response = await api.post('/assignments', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });

    const newAssignment = {
      id: response.data.assignment._id,
      config,
      sections: [],
      createdAt: Date.now(),
      backendId: response.data.assignment._id,
      processingStatus: response.data.assignment.processingStatus || 'pending',
      gradingSchemaStatus: response.data.assignment.gradingSchemaStatus || 'pending',
      gradingSchema: response.data.assignment.gradingSchema
    };

    setAssignments(prev => [...prev, newAssignment]);
    setActiveAssignmentId(newAssignment.id);

    return newAssignment;
  }, [getToken]);

  // Update assignment
  const updateAssignment = useCallback(async (updatedAssignment, skipBackendSync = false) => {
    setAssignments(prev => prev.map(a => a.id === updatedAssignment.id ? updatedAssignment : a));

    if (!skipBackendSync && updatedAssignment.backendId && updatedAssignment.sections) {
      try {
        const token = await getToken();
        await api.put(`/assignments/${updatedAssignment.backendId}`, {
          title: updatedAssignment.config?.title,
          sections: updatedAssignment.sections
        }, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
      } catch (error) {
        console.error('Failed to sync sections to backend:', error);
      }
    }
  }, [getToken]);

  // Delete assignment
  const deleteAssignment = useCallback(async (id) => {
    const token = await getToken();
    await api.delete(`/assignments/${id}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
    
    setAssignments(prev => prev.filter(a => a.id !== id));
    setActiveAssignmentId(prev => prev === id ? null : prev);
  }, [getToken]);

  // Select assignment
  const selectAssignment = useCallback((id) => {
    setActiveAssignmentId(id);
  }, []);

  // Clear assignments on logout
  const clearAssignments = useCallback(() => {
    if (user?.id) {
      localStorage.removeItem(`${ACTIVE_ASSIGNMENT_KEY}-${user.id}`);
    }
    setAssignments([]);
    setActiveAssignmentId(null);
    setUserUsage(null);
  }, [user?.id]);

  // Get active assignment
  const activeAssignment = assignments.find(a => a.id === activeAssignmentId);

  const value = {
    // State
    assignments,
    activeAssignmentId,
    activeAssignment,
    userUsage,
    isLoadingAssignments,

    // Actions
    setAssignments,
    setActiveAssignmentId,
    createAssignment,
    updateAssignment,
    deleteAssignment,
    selectAssignment,
    clearAssignments,
    setUserUsage,
    refreshAssignments: fetchFromBackend,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

export default AppContext;
